import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/Dashboard.css';

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('active-users');
  const [devModeEnabled, setDevModeEnabled] = useState(() => {
    return localStorage.getItem('devModeEnabled') === 'true';
  });
  const [loading, setLoading] = useState(true);
  const [showCreateTestUser, setShowCreateTestUser] = useState(false);
  const [newTestUser, setNewTestUser] = useState({
    displayName: '',
    email: '',
    role: 'loanee'
  });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      await Promise.all([fetchUsers(), fetchLoans(), fetchLoanRequests()]);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers() {
    const snapshot = await getDocs(collection(db, 'users'));
    const usersList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setUsers(usersList);
  }

  async function fetchLoans() {
    const snapshot = await getDocs(collection(db, 'loans'));
    const loansList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setLoans(loansList);
  }

  async function fetchLoanRequests() {
    const snapshot = await getDocs(collection(db, 'loanRequests'));
    const requestsList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setLoanRequests(requestsList);
  }

  async function handleAssignedLoanerChange(requestId, newLoanerId) {
    try {
      const loaner = users.find(u => u.id === newLoanerId);
      if (!loaner) return;

      await updateDoc(doc(db, 'loanRequests', requestId), {
        assignedLoanerId: newLoanerId,
        assignedLoanerName: loaner.displayName,
        updatedAt: new Date().toISOString()
      });
      fetchLoanRequests();
    } catch (err) {
      console.error('Error updating assigned loaner:', err);
      alert('Failed to update assigned loaner');
    }
  }

  async function handleRoleChange(userId, newRole) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date().toISOString()
      });
      fetchUsers();
    } catch (err) {
      console.error('Error updating user role:', err);
      alert('Failed to update user role');
    }
  }

  async function handleDeleteUser(userId, userName) {
    if (window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user');
      }
    }
  }

  async function handleDeleteLoan(loanId, loanInfo) {
    if (window.confirm(`Are you sure you want to delete loan for ${loanInfo}? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'loans', loanId));
        fetchLoans();
      } catch (err) {
        console.error('Error deleting loan:', err);
        alert('Failed to delete loan');
      }
    }
  }

  async function handleDeleteLoanRequest(requestId, requestInfo) {
    if (window.confirm(`Are you sure you want to delete loan request for ${requestInfo}? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'loanRequests', requestId));
        fetchLoanRequests();
      } catch (err) {
        console.error('Error deleting loan request:', err);
        alert('Failed to delete loan request');
      }
    }
  }

  function handleDevModeToggle() {
    const newValue = !devModeEnabled;
    setDevModeEnabled(newValue);
    localStorage.setItem('devModeEnabled', newValue.toString());
    alert(`Dev Mode ${newValue ? 'Enabled' : 'Disabled'}. Please refresh the page for changes to take effect.`);
  }

  function calculateTotalLoaned() {
    return loans.reduce((sum, loan) => sum + loan.amount, 0);
  }

  function calculateTotalOutstanding() {
    return loans.reduce((sum, loan) => sum + loan.remainingBalance, 0);
  }

  function calculateTotalPaid() {
    return calculateTotalLoaned() - calculateTotalOutstanding();
  }

  // Filter users - Active users are Google authenticated (not test users)
  const activeUsers = users.filter(u => 
    ['admin', 'loaner', 'loanee', 'new_user'].includes(u.role) && 
    !u.id?.startsWith('dev-') && 
    u.role !== 'test'
  );
  const testUsers = users.filter(u => u.role === 'test' || u.id?.startsWith('dev-'));
  
  // Get all loaners (including admins who have loaner capabilities)
  const allLoaners = users.filter(u => u.role === 'loaner' || u.role === 'admin');

  async function handleCreateTestUser() {
    if (!newTestUser.displayName || !newTestUser.email) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const userId = `dev-${Date.now()}`;
      await setDoc(doc(db, 'users', userId), {
        email: newTestUser.email,
        displayName: newTestUser.displayName,
        photoURL: `https://ui-avatars.com/api/?name=${encodeURIComponent(newTestUser.displayName)}&background=0572CE&color=fff`,
        role: newTestUser.role,
        createdAt: new Date().toISOString(),
        isTestUser: true
      });

      setNewTestUser({ displayName: '', email: '', role: 'loanee' });
      setShowCreateTestUser(false);
      fetchUsers();
      alert('Test user created successfully!');
    } catch (err) {
      console.error('Error creating test user:', err);
      alert('Failed to create test user');
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
          <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
            <input
              type="checkbox"
              checked={devModeEnabled}
              onChange={handleDevModeToggle}
              style={{width: '20px', height: '20px', cursor: 'pointer'}}
            />
            <span style={{fontWeight: '500'}}>Enable Dev Mode</span>
          </label>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Active Users</h3>
          <p className="stat-value">{activeUsers.length}</p>
        </div>
        <div className="stat-card">
          <h3>Test Users</h3>
          <p className="stat-value">{testUsers.length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Loans</h3>
          <p className="stat-value">{loans.length}</p>
        </div>
        <div className="stat-card">
          <h3>Pending Requests</h3>
          <p className="stat-value">{loanRequests.filter(r => r.status === 'pending').length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Loaned</h3>
          <p className="stat-value">${calculateTotalLoaned().toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Total Outstanding</h3>
          <p className="stat-value">${calculateTotalOutstanding().toFixed(2)}</p>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'active-users' ? 'active' : ''}`}
          onClick={() => setActiveTab('active-users')}
        >
          Active Users ({activeUsers.length})
        </button>
        <button
          className={`tab ${activeTab === 'test-users' ? 'active' : ''}`}
          onClick={() => setActiveTab('test-users')}
        >
          Test Users ({testUsers.length})
        </button>
        <button
          className={`tab ${activeTab === 'loans' ? 'active' : ''}`}
          onClick={() => setActiveTab('loans')}
        >
          All Loans ({loans.length})
        </button>
        <button
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Loan Requests ({loanRequests.length})
        </button>
      </div>

      {activeTab === 'active-users' && (
        <div className="tab-content">
          <h2>Active Users</h2>
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.displayName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className="select-field-small"
                        >
                          <option value="new_user">New User</option>
                          <option value="admin">Admin</option>
                          <option value="loaner">Loaner</option>
                          <option value="loanee">Loanee</option>
                        </select>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteUser(user.id, user.displayName)}
                          style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'test-users' && (
        <div className="tab-content">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <h2>Test Users</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateTestUser(true)}
            >
              + Create Test User
            </button>
          </div>

          {showCreateTestUser && (
            <div className="modal-overlay" onClick={() => setShowCreateTestUser(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
                <button className="modal-close" onClick={() => setShowCreateTestUser(false)}>×</button>
                <h3>Create Test User</h3>
                <div style={{marginTop: '1rem'}}>
                  <div className="input-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      className="input-field"
                      value={newTestUser.displayName}
                      onChange={(e) => setNewTestUser({...newTestUser, displayName: e.target.value})}
                      placeholder="Test User Name"
                    />
                  </div>
                  <div className="input-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="input-field"
                      value={newTestUser.email}
                      onChange={(e) => setNewTestUser({...newTestUser, email: e.target.value})}
                      placeholder="testuser@example.com"
                    />
                  </div>
                  <div className="input-group">
                    <label>Role</label>
                    <select
                      className="select-field"
                      value={newTestUser.role}
                      onChange={(e) => setNewTestUser({...newTestUser, role: e.target.value})}
                    >
                      <option value="admin">Admin</option>
                      <option value="loaner">Loaner</option>
                      <option value="loanee">Loanee</option>
                    </select>
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem', marginTop: '1.5rem'}}>
                    <button 
                      className="btn btn-primary"
                      onClick={handleCreateTestUser}
                      style={{flex: 1}}
                    >
                      Create User
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowCreateTestUser(false)}
                      style={{flex: 1}}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.displayName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDeleteUser(user.id, user.displayName)}
                        style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'loans' && (
        <div className="tab-content">
          <h2>All Loans</h2>
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Loaner</th>
                  <th>Loanee</th>
                  <th>Amount</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loans.map(loan => (
                  <tr key={loan.id}>
                    <td>{loan.loanerName}</td>
                    <td>{loan.loaneeName}</td>
                    <td>${loan.amount.toFixed(2)}</td>
                    <td>${loan.remainingBalance.toFixed(2)}</td>
                    <td>
                      <span className={`status-badge status-${loan.status === 'active' ? 'active' : loan.status === 'paid_off' ? 'paid' : 'pending'}`}>
                        {loan.status}
                      </span>
                    </td>
                    <td>{new Date(loan.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDeleteLoan(loan.id, `${loan.loaneeName} - $${loan.amount.toFixed(2)}`)}
                        style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="tab-content">
          <h2>Loan Requests</h2>
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Loanee</th>
                  <th>Amount</th>
                  <th>Purpose</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loanRequests.map(request => (
                  <tr key={request.id}>
                    <td>{request.loaneeName}</td>
                    <td>${request.amount.toFixed(2)}</td>
                    <td>{request.purpose}</td>
                    <td>
                      <span className="status-badge status-pending">
                        {request.status}
                      </span>
                    </td>
                    <td>
                      <select
                        value={request.assignedLoanerId || ''}
                        onChange={(e) => handleAssignedLoanerChange(request.id, e.target.value)}
                        className="select-field-small"
                        style={{minWidth: '150px'}}
                      >
                        <option value="">-- Unassigned --</option>
                        {allLoaners.map(loaner => (
                          <option key={loaner.id} value={loaner.id}>
                            {loaner.displayName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDeleteLoanRequest(request.id, `${request.loaneeName} - $${request.amount.toFixed(2)}`)}
                        style={{padding: '0.25rem 0.5rem', fontSize: '0.875rem'}}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
